import React, { useState, useEffect, useRef } from 'react';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Card } from 'primereact/card';
import settingsService from '../../services/SettingsService';

export default function ViewSettingsPage() {
    const [mounted, setMounted] = useState(false);
    useEffect(() => { setMounted(true); }, []);

    const [customerViewPreference, setCustomerViewPreference] = useState('PRODUCT');
    const [supplierViewPreference, setSupplierViewPreference] = useState('PRODUCT');
    const [loading, setLoading] = useState(false);
    const toast = useRef(null);

    const loadSettings = async () => {
        try {
            setLoading(true);
            const res = await settingsService.getTenantSettings();
            if (res.data && res.data.success) {
                const settings = res.data.data;
                if (settings["CustomerDetailViewPreference"]) {
                    setCustomerViewPreference(settings["CustomerDetailViewPreference"]);
                }
                if (settings["SupplierDetailViewPreference"]) {
                    setSupplierViewPreference(settings["SupplierDetailViewPreference"]);
                }
            }
        } catch (error) {
            console.error("Ayarlar yüklenirken hata oluştu:", error);
            toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Ayarlar yüklenemedi.' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadSettings();
    }, []);

    const handleSave = async () => {
        try {
            setLoading(true);
            const resCustomer = await settingsService.saveTenantSetting({
                settingKey: "CustomerDetailViewPreference",
                settingValue: customerViewPreference
            });
            const resSupplier = await settingsService.saveTenantSetting({
                settingKey: "SupplierDetailViewPreference",
                settingValue: supplierViewPreference
            });

            if (resCustomer.data?.success && resSupplier.data?.success) {
                toast.current.show({ severity: 'success', summary: 'Başarılı', detail: 'Görünüm ayarları kaydedildi.' });
            } else {
                toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Ayarlar kaydedilemedi.' });
            }
        } catch (error) {
            console.error("Ayarlar kaydedilirken hata:", error);
            toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Bir hata oluştu.' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="p-2 space-y-4 animate-fade-in bg-[#f8fafc] min-h-screen">
            <Toast ref={toast} appendTo={mounted ? document.body : "self"} />

            <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-3">
                    <div className="!bg-blue-600 p-4 rounded-xl shadow-lg shadow-blue-500/20">
                        <i className="pi pi-sliders-h text-white text-xl"></i>
                    </div>
                    <div>
                        <h1 className="text-2xl font-black text-slate-900 uppercase tracking-tight">Görünüm Ayarları</h1>
                        <p className="text-slate-500 text-sm font-medium">Sistem ekranlarının görünüm ve yükleme tercihlerini buradan özelleştirebilirsiniz.</p>
                    </div>
                </div>
            </div>

            <div className="max-w-3xl">
                <Card className="border-round-xl shadow-1 p-4">
                    {/* Müşteri Detay Tercihi */}
                    <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200 mb-6">
                        <h3 className="text-sm font-bold text-slate-700 mb-2">Müşteri Detay Ekranı Varsayılan Görünümü</h3>
                        <p className="text-xs text-slate-500 mb-4 leading-relaxed">
                            Müşteri detay sayfasına girdiğinizde öncelikli olarak hangi verilerin yüklenmesini istediğinizi seçin. 
                            <strong> Fatura Odaklı</strong> seçildiğinde sunucu tarafında alınan ürünler listelenmeyecek ve sayfa çok daha hızlı açılacaktır.
                        </p>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div 
                                className={`p-4 rounded-xl border-2 cursor-pointer transition-all flex flex-col justify-between ${
                                    customerViewPreference === 'INVOICE' 
                                        ? 'border-blue-600 bg-blue-50/50 shadow-sm' 
                                        : 'border-slate-200 bg-white hover:border-slate-300'
                                }`}
                                onClick={() => setCustomerViewPreference('INVOICE')}
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <span className="font-black text-xs uppercase tracking-wider text-slate-700">Fatura Odaklı</span>
                                    <i className={`pi ${customerViewPreference === 'INVOICE' ? 'pi-check-circle text-blue-600' : 'pi-circle'} text-lg`}></i>
                                </div>
                                <p className="text-[11px] text-slate-500">Faturalar sekmesi varsayılan gelir. Ürün listesi çekilmediği için sunucuyu yormaz, ekran anında yüklenir.</p>
                            </div>

                            <div 
                                className={`p-4 rounded-xl border-2 cursor-pointer transition-all flex flex-col justify-between ${
                                    customerViewPreference === 'PRODUCT' 
                                        ? 'border-blue-600 bg-blue-50/50 shadow-sm' 
                                        : 'border-slate-200 bg-white hover:border-slate-300'
                                }`}
                                onClick={() => setCustomerViewPreference('PRODUCT')}
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <span className="font-black text-xs uppercase tracking-wider text-slate-700">Ürün Odaklı</span>
                                    <i className={`pi ${customerViewPreference === 'PRODUCT' ? 'pi-check-circle text-blue-600' : 'pi-circle'} text-lg`}></i>
                                </div>
                                <p className="text-[11px] text-slate-500">Alınan Ürünler sekmesi varsayılan gelir. Müşteriye satılan tüm ürün kalemleri detaylıca sorgulanıp listelenir.</p>
                            </div>
                        </div>
                    </div>

                    {/* Tedarikçi Detay Tercihi */}
                    <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200 mb-6">
                        <h3 className="text-sm font-bold text-slate-700 mb-2">Tedarikçi Detay Ekranı Varsayılan Görünümü</h3>
                        <p className="text-xs text-slate-500 mb-4 leading-relaxed">
                            Tedarikçi detay sayfasına girdiğinizde öncelikli olarak hangi verilerin yüklenmesini istediğinizi seçin. 
                            <strong> Fatura Odaklı</strong> seçildiğinde sunucu tarafında alınan ürünler listelenmeyecek ve sayfa çok daha hızlı açılacaktır.
                        </p>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div 
                                className={`p-4 rounded-xl border-2 cursor-pointer transition-all flex flex-col justify-between ${
                                    supplierViewPreference === 'INVOICE' 
                                        ? 'border-blue-600 bg-blue-50/50 shadow-sm' 
                                        : 'border-slate-200 bg-white hover:border-slate-300'
                                }`}
                                onClick={() => setSupplierViewPreference('INVOICE')}
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <span className="font-black text-xs uppercase tracking-wider text-slate-700">Fatura Odaklı</span>
                                    <i className={`pi ${supplierViewPreference === 'INVOICE' ? 'pi-check-circle text-blue-600' : 'pi-circle'} text-lg`}></i>
                                </div>
                                <p className="text-[11px] text-slate-500">Faturalar sekmesi varsayılan gelir. Ürün listesi çekilmediği için sunucuyu yormaz, ekran anında yüklenir.</p>
                            </div>

                            <div 
                                className={`p-4 rounded-xl border-2 cursor-pointer transition-all flex flex-col justify-between ${
                                    supplierViewPreference === 'PRODUCT' 
                                        ? 'border-blue-600 bg-blue-50/50 shadow-sm' 
                                        : 'border-slate-200 bg-white hover:border-slate-300'
                                }`}
                                onClick={() => setSupplierViewPreference('PRODUCT')}
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <span className="font-black text-xs uppercase tracking-wider text-slate-700">Ürün Odaklı</span>
                                    <i className={`pi ${supplierViewPreference === 'PRODUCT' ? 'pi-check-circle text-blue-600' : 'pi-circle'} text-lg`}></i>
                                </div>
                                <p className="text-[11px] text-slate-500">Alınan Ürünler sekmesi varsayılan gelir. Tedarikçiden alınan tüm ürün kalemleri detaylıca sorgulanıp listelenir.</p>
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end gap-2">
                        <Button
                            label="AYARLARI KAYDET"
                            icon="pi pi-save"
                            className="p-button-primary rounded-xl font-black px-6 py-3 shadow-md"
                            loading={loading}
                            onClick={handleSave}
                        />
                    </div>
                </Card>
            </div>
        </div>
    );
}
